import Config from 'config';
let config;
import mediator, { ACTIONS } from 'mediator';
import fastdom from 'fastdom';
import features from 'lib/common/features';
import { find, has, includes } from 'lodash';
import svgClasses from 'lib/common/svgClasses';
import renderCustomIcon from 'lib/seats/renderCustomIcon';
import { getSeatType, getAvailableSeatTypes } from 'lib/seats/seatTypes';
import { getColorCode } from 'lib/common/colorCode';
import pricesForZone from 'lib/pricing/pricesForZone';
import { toCurrency } from 'lib/common/displayHelpers';
import ReactTooltip from 'react-tooltip';
import isMobile from 'lib/common/isMobile';
import { CLASS_NAMES } from 'constants';

function getClassNames(data) {
  config = new Config().getConfig();
  let classNames = [CLASS_NAMES.base];
  let statusCode = data.seat_status.status_code;
  let seatType = getSeatType(statusCode);

  classNames.push(`seat--${seatType.slug}`);

  /**
   * Seats with specific status codes should be hidden
   */
  if (
    includes(config.seatTypes.hiddenStatusCodes, data.seat_status.status_code)
  ) {
    classNames.push(CLASS_NAMES.killed);
  }

  /**
   * Apply status class based on seat type id
   */
  let seatTypes = getAvailableSeatTypes();
  seatTypes.forEach(function(type) {
    if (
      has(type, 'seatTypeIds') &&
      includes(type.seatTypeIds, data.seat_type)
    ) {
      classNames.push(`seat--${seatType.slug}`);
    }
  });

  return classNames;
}

function clickHandler(el, data) {
  el.addEventListener('click', function() {
    mediator.publish(ACTIONS.SEAT_SELECTED, { seatEl: el, seatData: data });
  });
}

function getSeatText(icon) {
  if (icon.customName === 'custom-icon-ada') {
    return `<p>WheelChair Space<br/>`;
  }

  if (icon.customName === 'custom-icon-companion') {
    return `<p>Companion Space<br/>`;
  }

  return '';
}

function injectCustomIconElement(type, seat, classNames, colours, seatPrices) {
  let newEl;
  try {
    newEl = renderCustomIcon(
      type.customName,
      seat.el,
      classNames,
      getColorCode(seat.data.zone_no, colours),
      seatPrices
    );
  } catch (e) {} // eslint-disable-line no-empty

  if (newEl) {
    // Set an id on the new seat icon for later retrieval.
    newEl.id = `syos-custom-icon-${seat.data.seat_no}`;

    // Hide the old element
    svgClasses.addClass(seat.el, CLASS_NAMES.killed);

    // Re-add the click handler to the new element
    clickHandler(newEl, seat.data);

    return newEl;
  } else {
    return seat.el;
  }
}

function enhanceSeatEls(seats, colours, prices) {
  let customSeatTypes = getAvailableSeatTypes().filter(function(x) {
    return x.method === 'custom' || has(x, 'priceTypeCategories');
  });

  seats.map(function(seat) {
    if (seat.data && seat.el) {
      let dataTip = '';
      let colourCode = getColorCode(seat.data.zone_no, colours);
      let classNames = getClassNames(seat.data);

      classNames.forEach(function(name) {
        svgClasses.addClass(seat.el, name);
        if (seat.data.seat_status.description === 'Available') {
          if (colourCode) {
            if (features.isEnabled('seatColours')) {
              seat.el.setAttribute('style', `stroke: ${colourCode}`);
            }
          } else {
            svgClasses.removeClass(seat.el, 'seat--available');
            svgClasses.addClass(seat.el, 'seat--unavailable');
          }
        }
      });

      let seatPrices = pricesForZone(prices, seat.data.zone_no);

      clickHandler(seat.el, seat.data);

      let matchingIconForPriceTypeCategory = find(customSeatTypes, function(
        type
      ) {
        if (has(type, 'customName') && has(type, 'priceTypeCategories')) {
          return seatPrices.some(seatPrice => {
            return type.priceTypeCategories.indexOf(seatPrice.category) > -1;
          });
        }

        return false;
      });

      if (matchingIconForPriceTypeCategory) {
        svgClasses.addClass(seat.el, 'seat--promo');
        classNames.push('seat--promo');
        // seat.el = injectCustomIconElement(
        //   matchingIconForPriceTypeCategory,
        //   seat,
        //   classNames,
        //   colours,
        //   seatPrices
        // );

        //dataTip += getSeatText(matchingIconForPriceTypeCategory);
      }

      let matchingIconForHoldCode = find(customSeatTypes, function(type) {
        return (
          has(type, 'customName') &&
          has(type, 'holdCodes') &&
          includes(type.holdCodes, seat.data.hc_no)
        );
      });

      if (matchingIconForHoldCode && colourCode) {
        seat.el = injectCustomIconElement(
          matchingIconForHoldCode,
          seat,
          classNames,
          colours,
          seatPrices
        );

        dataTip += getSeatText(matchingIconForHoldCode);
      }

      let matchingIconForSeatType = find(customSeatTypes, function(type) {
        return (
          has(type, 'customName') &&
          has(type, 'seatTypeIds') &&
          includes(type.seatTypeIds, seat.data.seat_type)
        );
      });

      if (matchingIconForSeatType && colourCode) {
        seat.el = injectCustomIconElement(
          matchingIconForSeatType,
          seat,
          classNames,
          colours,
          seatPrices
        );

        dataTip += getSeatText(matchingIconForSeatType);
      }

      seatPrices.forEach(function(price) {
        dataTip += `<span class="type">${price.price_type_desc}</span>`;
        dataTip += `<span class="price">${toCurrency(price.price)}</span>`;
      });

      seat.el.setAttribute('data-tip', dataTip);
      seat.el.setAttribute('data-for', 'seat-detail');

      if (!isMobile()) {
        seat.el.addEventListener('mouseenter', () => {
          if (svgClasses.hasClass(seat.el, CLASS_NAMES.selected)) {
            let lineItem = document.getElementById(
              `line-item-${seat.data.seat_no}`
            );
            if (lineItem && !lineItem.classList.contains('hover')) {
              lineItem.classList.add('hover');
            }

            if (!document.body.classList.contains('line-item-tooltip')) {
              document.body.classList.add('line-item-tooltip');
            }

            let dataTip = `${
              seatPrices[0] !== undefined ? seatPrices[0].zone_desc : ''
            } ${seat.data.seat_row}, ${seat.data.seat_num}`;

            seat.el.setAttribute('data-tip', dataTip);
          }
          ReactTooltip.show(seat.el);
        });

        seat.el.addEventListener('mouseleave', () => {
          if (svgClasses.hasClass(seat.el, CLASS_NAMES.selected)) {
            let lineItem = document.getElementById(
              `line-item-${seat.data.seat_no}`
            );

            if (lineItem && lineItem.classList.contains('hover')) {
              lineItem.classList.remove('hover');
            }

            if (document.body.classList.contains('line-item-tooltip')) {
              document.body.classList.remove('line-item-tooltip');
            }

            seat.el.setAttribute('data-tip', dataTip);
          }

          ReactTooltip.hide(seat.el);
        });
      }
    }

    return seat;
  });

  return seats;
}

function priceFilters(seats) {
  mediator.subscribe(ACTIONS.PRICE_FILTER_SELECTED, function(payload) {
    const selected = payload.selected;

    if (selected.length) {
      const otherSeats = seats.filter(seat => {
        return has(seat, 'data.zone_no')
          ? selected.indexOf(seat.data.zone_no) === -1
          : false;
      });

      fastdom.mutate(function() {
        seats.forEach(function(seat) {
          svgClasses.removeClass(seat.el, CLASS_NAMES.hidden);
        });

        otherSeats.forEach(function(seat) {
          svgClasses.addClass(seat.el, CLASS_NAMES.hidden);
        });
      });
    } else {
      fastdom.mutate(function() {
        seats.forEach(function(seat) {
          svgClasses.removeClass(seat.el, CLASS_NAMES.hidden);
        });
      });
    }
  });
}

/**
 * Toggle seat selection style
 * Need to a bit verbose with checks to avoid cases where
 * multiple events are triggered and cause double toggling
 */
function toggleSelected(seat, isSelected) {
  let element = seat.seatEl;
  if (seat.customSeatEl !== undefined && seat.customSeatEl !== null) {
    element = seat.customSeatEl;
  }

  if (isSelected && !svgClasses.hasClass(element, CLASS_NAMES.selected)) {
    svgClasses.addClass(element, CLASS_NAMES.selected);
  }

  if (!isSelected && svgClasses.hasClass(element, CLASS_NAMES.selected)) {
    svgClasses.removeClass(element, CLASS_NAMES.selected);
  }

  if (!svgClasses.hasClass(element, CLASS_NAMES.available)) {
    svgClasses.addClass(element, CLASS_NAMES.available);
  }
}

function selectionListeners() {
  mediator.subscribe(ACTIONS.SEAT_ADDED, function(seat) {
    seat && toggleSelected(seat, true);
  });
  mediator.subscribe(ACTIONS.SEAT_TOGGLE_SELECTED, function(seat) {
    seat && toggleSelected(seat, true);
  });
  mediator.subscribe(ACTIONS.SEAT_DESELECTED, function(seat) {
    seat && toggleSelected(seat, false);
  });
}

export default function(seats, colours, prices) {
  let enhancedSeats = enhanceSeatEls(seats, colours, prices);

  priceFilters(seats);
  selectionListeners();

  return enhancedSeats;
}



// WEBPACK FOOTER //
// ./src/javascripts/behaviours/enhanceSeats.js